// --- Core ---
import {FC, ReactElement, ReactNode} from "react";
import {FormattedMessage} from "react-intl";
import PropTypes from "prop-types";
import {first, isString, map} from "underscore";
// --- Material UI ---
import {Link, Tooltip, Typography, TypographyProps} from '@mui/material';
// Colors
import {grey} from "@mui/material/colors";
import {Truncate, TruncateProps} from "shared/Truncate";

const newLineRegex = /\r?\n/;

/**
 * Test if a string is empty or only whitespaces
 *
 * @return boolean
 */
function isEmpty(str) {
  if (typeof str === "undefined" || typeof str !== "string") {
    return true;
  }

  return str.trim() === "";
}
/**
 * Test if the string has one or more line breaks
 */
const isMultiline = (test: string): boolean => newLineRegex.test(test);

interface EmptyPlaceholderProps extends TruncateProps {
  /**
   *  The text to display
   */
  text?: string;
  /**
   * The placeholder in case the text is empty
   * @default "No Information" (Localized)
   */
  placeholder?: ReactNode;
  /**
   * A string placeholder in case the text is empty. Has the same style as the default text
   * @default "No Information" (Localized)
   */
  placeholderText?: ReactNode;
}

/**
 * Show a string or a placeholder in case the string is empty.
 *
 * @see isEmpty
 */
const EmptyPlaceholder = ({text = "", placeholder, placeholderText, ...rest}: EmptyPlaceholderProps) => {
  if (isEmpty(text)) {
    return (
      placeholder ?
        <>{placeholder}</> :
        <i style={{color: grey[500]}}>
          {placeholderText || <FormattedMessage id="patients.table.entry.nothing" defaultMessage="No information"/>}
        </i>
    );
  }

  return <Truncate text={text} {...rest}/>;
};

/**
 * Get the first line of a string, or the complete text when no line breaks present
 */
const firstLine = (str: string, n: number = 1): string => first(str.split(newLineRegex, n), n).join("\n");

interface FormatBooleanProps {
  value: boolean;
}

const FormattedBoolean: FC<FormatBooleanProps> = ({value}) =>
  value ? <FormattedMessage id="yes" defaultMessage="Yes"/> : <FormattedMessage id="no" defaultMessage="No"/>;

function truncate(value: string, numChars: number): string {
  return value.length <= numChars ? value : (value.slice(0, numChars - 3) + '...');
}

/**
 * Truncate a string to a given amount of characters.
 *
 * Does not have a truncated and extended mode. Instead, the full text is shown
 * with a tooltip.
 *
 * @see Truncate
 */
function TruncateTag({string, numChars}) {
  if (string.length <= numChars) {
    return string;
  }

  return (
    <Tooltip title={string}>
      {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
      <span>{string.slice(0, numChars - 3) + '...'}</span>
    </Tooltip>
  );
}

TruncateTag.propTypes = {
  /** String that will be truncated */
  string: PropTypes.string.isRequired,
  /** Number of characters to which the string is truncated. */
  numChars: PropTypes.number.isRequired
};

interface UnknownProps {
  /**
   * The unknown value
   */
  value: string;
}

/**
 * Get an unknown value as string. For example a key in an enum
 */
const unknown = (intl, value) => intl.formatMessage({id: "utils.unknown", defaultMessage: "Unknown ({value})"}, {value});
/**
 * Display an unknown value. For example a key in an enum
 */
const Unknown = ({value}: UnknownProps) => <FormattedMessage id="utils.unknown" defaultMessage="Unknown ({value})" values={{value}}/>;

interface MultilineProps extends TypographyProps {
  /**
   * String that will be split in multiple lines
   */
  children: string;
}

/**
 * Get array of lines from a text with (ASCII) newlines.
 *
 * All other props are applied to the underlying Typography.
 *
 * @see Typography
 */
const Multiline: FC<TypographyProps> = ({children, ...other}: MultilineProps) =>
  //NOTE(Keune): Split on a newline. this can either be \r\n or \n depending on the client's OS
  <>{map(children.split(newLineRegex), (line, index) => <Typography key={index} {...other}>{line}</Typography>)}</>;

interface PhoneNumberProps {
  errorPlaceholder?: ReactElement;
  children?: ReactElement;
  number: string;
  tooltip?: ReactNode;
}

/**
 * Rendere eine Telefonnummer als Link
 *
 * Rendert die über props.number übergebene Telefonnummer als Link mit dem "tel:" Protokoll,
 * wenn sie dem Format für global-number-digits oder local-number-digits aus RFC3966 entspricht.
 * Als zusätzliche Trennzeichen sind "/" und " " erlaubt.
 *
 * Wenn die Nummer nicht dem erwarteten Format entspricht, wird sie an EmptyPlaceholder übergeben.
 *
 * Quelle: https://tools.ietf.org/html/rfc3966
 */
const PhoneNumber = ({number, tooltip, children, errorPlaceholder}: PhoneNumberProps) => {
  // NOTE(Keune): Entferne Leerzeichen und /, welche typischerweise als Trennzeichen verwendet
  // werden, aber laut RFC3966 nicht erlaubt sind
  const cleanNumber = cleanPhoneNumber(number);

  if (/^[+]?[\d\-,()]+$/.test(cleanNumber)) {
    if (children) {
      return (
        <Link href={`tel:${cleanNumber}`}>
          <Tooltip
            title={
              tooltip ||
              <FormattedMessage id="phonenumber.call" defaultMessage="Call patient<br></br>{number}" values={{number: number}}/>
            }
          >
            {children}
          </Tooltip>
        </Link>
      );
    }

    return (
      <Link href={`tel:${cleanNumber}`}>
        {number}
      </Link>
    );
  }

  return errorPlaceholder || <EmptyPlaceholder text={number}/>;
};

const cleanPhoneNumber = (number: string) => number ? number.replace(/[/\s]/g, "") : "";

/**
 * Convert a ruby class name to the snake_case representation used by the serializer
 */
const rubyClassToSnakeCase = (text: string) => {
  if (isString(text)) {
    //NOTE(Keune): When the text is beginning with an uppercase letter, the first replace puts a _ at the beginning
    return text.replace(/(::)?[A-Z]/g, k => `_${k.slice(-1).toLowerCase()}`).replace(/^_/, '');
  }

  return "";
};

/**
 * Convert the given text from snake_case to camelCase
 *
 * @param text The text to be converted
 */
const toCamel = (text: string) => text.replace(/(_\w)/g, k => k[1].toUpperCase());

/**
 * Returns an empty string if the given value is null or undefined,
 * otherwise returns the given value.
 *
 * @param {string} [value] - The value to be checked for null or undefined.
 * @returns {string} - The empty string if value is null or undefined,
 *    otherwise returns the given value.
 */
const nullString = (value) => {
  if (value === null || value === undefined) {
    return '';
  } else {
    return value;
  }
};

export {
  isEmpty,
  isMultiline,
  EmptyPlaceholder,
  firstLine,
  FormattedBoolean,
  Multiline,
  truncate,
  TruncateTag,
  unknown,
  Unknown,
  PhoneNumber,
  rubyClassToSnakeCase,
  toCamel,
  nullString,
  cleanPhoneNumber
};
